import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { SecretField } from '@/shared/ui/Field';

import { useAcceptInvitation, useCheckInvitation } from '../api/auth';
import { tokenFromQuery } from '../model/verification';
import { passwordProblem } from '../model/activation';

const MIN_PASSWORD_LENGTH = 10;

/* Activating an invited account: one screen, two steps, one token.
 *
 * **The brief asks for `/activate` to validate and then redirect to `/set-password`, and this does
 * both in one route rather than two.** The reason is the token: a redirect would have to carry it,
 * and the only two places to put it are the URL — where it lands in browser history and in any
 * proxy log — or client state, which does not survive the reload a redirect invites. Keeping the
 * person on one address means the token is read from the query string once, posted in a body twice,
 * and never written anywhere else.
 *
 * The two steps are still distinct and both are visible: the link is validated on mount (which is
 * what marks the address proved), and only then is a password collected. A reader who arrives with
 * a dead link never sees the form, which is the point of validating first.
 *
 * **Five states, and collapsing any two sends somebody to the wrong place.**
 *
 * * *No token in the URL* — mangled by a mail client, or copied without its query string.
 *   Deliberately not the same message as a refused token: that one means the platform read a token
 *   and refused it, which is nearly always expiry, and telling this reader their invitation expired
 *   sends them to ask for another that will arrive equally broken.
 * * *Validating* — a message rather than a blank card, because this screen does its first work on
 *   mount and a reader who sees nothing assumes the link is dead.
 * * *Refused* — the server's own sentence, which is one identical message for five facts on
 *   purpose. The screen does not try to improve on it, and it already names the fix.
 * * *Collecting a password* — the form.
 * * *Activated* — handled by the caller, which signs the person in and navigates.
 */
export function ActivateAccountPanel({
  onActivated,
}: {
  readonly onActivated: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { search } = useLocation();
  const parsed = tokenFromQuery(search);
  const token = parsed.kind === 'present' ? parsed.token : null;
  const invitation = useCheckInvitation(token);
  const accept = useAcceptInvitation();

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [attempted, setAttempted] = useState(false);

  /* Through a ref so `onActivated` is not a dependency below: callers pass an inline arrow, and as
     a dependency a new identity on every render would re-run this effect — which navigates. That
     is the defect `VerifyEmailPanel`'s countdown had. */
  const done = useRef(onActivated);
  done.current = onActivated;

  useEffect(() => {
    if (accept.isSuccess) done.current();
  }, [accept.isSuccess]);

  const problem = passwordProblem({
    password,
    confirmation,
    minLength: MIN_PASSWORD_LENGTH,
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setAttempted(true);
    if (problem !== null || token === null) return;
    accept.mutate({ token, password });
  };

  if (parsed.kind === 'missing') {
    return (
      <>
        <h1 className="lq-auth__heading">{t('auth.activate.title')}</h1>
        <Alert tone="error" title={t('auth.activate.missing.title')}>
          {t('auth.activate.missing.body')}
        </Alert>
      </>
    );
  }

  if (invitation.isPending) {
    return (
      <>
        <h1 className="lq-auth__heading">{t('auth.activate.title')}</h1>
        <Alert tone="notice" title={t('auth.activate.working.title')}>
          {t('auth.activate.working.body')}
        </Alert>
      </>
    );
  }

  if (invitation.isError) {
    return (
      <>
        <h1 className="lq-auth__heading">{t('auth.activate.title')}</h1>
        {/* A notice rather than an error: an expired or spent invitation is the expected refusal
            rather than a fault, and the server's sentence already names the fix. */}
        <Alert tone="notice" title={t('auth.activate.refused.title')}>
          {invitation.error.message}
        </Alert>
      </>
    );
  }

  return (
    <>
      <h1 className="lq-auth__heading">{t('auth.activate.title')}</h1>
      {/* **The address is shown, and it is the stored one rather than anything in the URL.**
          Somebody invited at a work address who clicked from a personal inbox needs to see which
          account they are activating — and it is safe to show, because they demonstrated
          possession of a token that names exactly this address. */}
      <Alert tone="ok" title={t('auth.activate.ready.title')}>
        {t('auth.activate.ready.body', { email: invitation.data.email })}
      </Alert>
      <form onSubmit={onSubmit}>
        <SecretField
          label={t('auth.field.password')}
          onChange={setPassword}
          hint={t('auth.signup.passwordHint', { minLength: MIN_PASSWORD_LENGTH })}
          required
        />
        <SecretField
          label={t('auth.activate.confirmLabel')}
          onChange={setConfirmation}
          required
        />
        {/* **Only after a submission attempt.** Telling somebody their password is too short while
            they are still typing the first character is a warning that is true and useless, and it
            trains a reader to ignore the one that matters. */}
        {attempted && problem !== null && (
          <Alert tone="warning">
            {t(`auth.activate.problem.${problem}`, { minLength: MIN_PASSWORD_LENGTH })}
          </Alert>
        )}
        {accept.isError && (
          <Alert tone="error" title={t('auth.activate.failedTitle')}>
            {accept.error.message}
          </Alert>
        )}
        <Button type="submit" tone="primary" size="block" busy={accept.isPending}>
          {t('auth.activate.submit')}
        </Button>
      </form>
    </>
  );
}
